import type { ReservationAnalyticsContext, ReservationAnalyticsEvent, ReservationAnalyticsEventType, ReservationAnalyticsSession } from './analyticsTypes.ts';
import { isValidIanaTimezone } from '../location/unitLocation.ts';

const detectDeviceType = (width: number): ReservationAnalyticsContext['deviceType'] => width <= 767 ? 'mobile' : width <= 1024 ? 'tablet' : 'desktop';
const parseBrowser = (ua: string) => /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'unknown';
const parseOs = (ua: string) => /Windows/i.test(ua) ? 'Windows' : /Mac OS/i.test(ua) ? 'macOS' : /Android/i.test(ua) ? 'Android' : /iPhone|iPad/i.test(ua) ? 'iOS' : /Linux/i.test(ua) ? 'Linux' : 'unknown';
const uuid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const collectReservationAnalyticsContext = (locale: string, unitTimezone?: string): ReservationAnalyticsContext => {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const win = typeof window !== 'undefined' ? window : undefined;
  const params = new URLSearchParams(win?.location?.search || '');
  const width = win?.innerWidth || 0;
  let clientTimezone = 'UTC';
  try {
    clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    // Analytics remains best-effort in restricted runtimes.
  }
  return {
    locale,
    language: nav?.language || locale,
    timezone: clientTimezone,
    ...(isValidIanaTimezone(unitTimezone) ? { unitTimezone } : {}),
    screen: { width: win?.screen?.width || 0, height: win?.screen?.height || 0 },
    viewport: { width, height: win?.innerHeight || 0 },
    deviceType: detectDeviceType(width),
    browser: parseBrowser(nav?.userAgent || ''),
    operatingSystem: parseOs(nav?.userAgent || ''),
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
};

export const buildReservationAnalyticsEvent = (input: {
  unitId: string;
  eventType: ReservationAnalyticsEventType;
  session: ReservationAnalyticsSession;
  context: ReservationAnalyticsContext;
  timestamp?: string;
}): ReservationAnalyticsEvent => ({
  eventId: uuid(),
  eventType: input.eventType,
  unitId: input.unitId.trim(),
  timestamp: input.timestamp || new Date().toISOString(),
  sessionId: input.session.sessionId,
  visitorId: input.session.visitorId,
  ...input.context,
  schemaVersion: 1,
});
